import { BadConnexionWarning } from '@core-components/BadConnexionWarning';
import { Suspense } from '@core-components/Suspense';
import { Toaster } from '@honestica/design-system';
import { Layout, StyleProvider } from '@honestica/ui-kit/src';
import { EnvManager } from '@services/environment';
import envVariables from '@services/environment/variables';
import { monitoring } from '@services/monitoring';
import { queryConfig } from '@services/react-query/react-query';
import { store } from '@store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Navigate, Route } from 'react-router-dom';
import { ReduxSagaContextMiddleware } from './components/App/reduxSagaContext';
import { IconSandbox } from './components/IconSandbox/IconSandbox.component';
import { NotificationWindow } from './components/NotificationWindow';
import { RouterLogger } from './components/RouterLogger/RouterLogger';
import { ViewportProvider } from './context/Viewport.context';
import { useNotifyNetworkOffline } from './hooks/useOffline.hook';
import { DesktopSettingsProvider } from './providers/DesktopSettings.provider';
import { EnvProvider } from './providers/Env.provider';
import { ErrorProvider } from './providers/Error.provider';
import { FeatureFlagsProvider } from './providers/FeatureFlags.provider';
import { I18nProvider } from './providers/I18n.provider';
import { UIKitProvider } from './providers/UIKit.provider';
import { UserProvider } from './providers/User.provider';
import { UserIdentitiesProvider } from './providers/UserIdentities.provider';

type ProvidersProps = {
  children: React.ReactNode;
};

const { Routes } = monitoring;

export function AppProviders({ children }: ProvidersProps) {
  useNotifyNetworkOffline();

  const basename = EnvManager.getBrowserHistoryName();

  const [queryClient] = useState(() => new QueryClient({ defaultOptions: queryConfig }));

  return (
    <Suspense>
      <Provider store={store}>
        <ErrorProvider>
          <EnvProvider>
            <QueryClientProvider client={queryClient}>
              {envVariables.NODE_ENV !== 'test' && (
                <ReactQueryDevtools buttonPosition="bottom-right" />
              )}
              <DesktopSettingsProvider>
                <ViewportProvider>
                  <I18nProvider>
                    <StyleProvider hashPriority="high">
                      <UIKitProvider>
                        <Layout>
                          <BadConnexionWarning />
                          <Toaster />
                          <BrowserRouter>
                            <RouterLogger />
                            <ReduxSagaContextMiddleware>
                              <Routes>
                                <Route path="/apps/notification" element={<NotificationWindow />} />
                                {['staging', 'dev', 'review'].includes(
                                  EnvManager.getName() ?? '',
                                ) && <Route path="/apps/icon-sandbox" element={<IconSandbox />} />}
                                <Route
                                  path={`${basename}/*`}
                                  element={
                                    <UserProvider>
                                      <UserIdentitiesProvider>
                                        <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
                                      </UserIdentitiesProvider>
                                    </UserProvider>
                                  }
                                />
                                {basename && (
                                  <Route path="*" element={<Navigate to={basename} replace />} />
                                )}
                              </Routes>
                            </ReduxSagaContextMiddleware>
                          </BrowserRouter>
                        </Layout>
                      </UIKitProvider>
                    </StyleProvider>
                  </I18nProvider>
                </ViewportProvider>
              </DesktopSettingsProvider>
            </QueryClientProvider>
          </EnvProvider>
        </ErrorProvider>
      </Provider>
    </Suspense>
  );
}
